import React, { useState } from "react";
import { Upload, X, FileText } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

const CSV_SAMPLE = `name,estimated_time,role,resource,order,order_type,lag,status,notes,po_number,invoice_number,vendor,vendor_contact,fulfillment_request_number
Discovery & Research,5,Project Manager,,,,,In Progress,Workshops with stakeholders,PO-1001,,Acme Research,jane@acme.example,FR-42
Information Architecture,5,UX Designer,,1,Start After,0,Not Started,Sitemap + wireframes
Visual Design,7,UX Designer,,2,Start After,0,Not Started,
Frontend Development,12,Senior Developer,,3,Start After,0,Not Started,
Launch,2,Project Manager,,4,Start After,0,Not Started,Go/no-go review`;

export default function TaskCsvImportButton({ projectId, onImported, locked, onLocked }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async ({ dryRun }) => {
    if (locked) { onLocked && onLocked(); return; }
    if (!csv.trim()) { toast.error("Paste some CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/tasks/import`, { data: { csv, dry_run: !!dryRun, replace } });
      setResult(data);
      if (!dryRun) {
        toast.success(`Imported ${data.created_count} task${data.created_count === 1 ? "" : "s"}`);
        setCsv("");
        setReplace(false);
        onImported && onImported();
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => { setOpen(true); setResult(null); }} data-testid="open-task-csv-btn"
        disabled={locked}
        className={`inline-flex items-center gap-2 border border-slate-700 ${locked ? "opacity-50 cursor-not-allowed" : "hover:border-blue-500"} text-slate-200 px-3 py-2 rounded-lg text-sm`}>
        <Upload size={14} /> Import tasks
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="task-csv-modal">
          <div className="pc-card w-full max-w-3xl mt-16">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <FileText className="text-blue-300" size={16}/>
                <div>
                  <div className="text-sm font-semibold text-white" style={{ fontFamily: "Outfit" }}>Import tasks from CSV</div>
                  <div className="text-xs text-slate-400">Headers (any order, case-insensitive): <code className="text-blue-300">name, estimated_time, role, resource, order, order_type, lag, status, notes, po_number, invoice_number, vendor, vendor_contact, fulfillment_request_number</code>. Role/Resource match by name.</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="close-task-csv-btn" className="text-slate-500 hover:text-white"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-widest text-slate-400">CSV content</label>
                <button onClick={() => setCsv(CSV_SAMPLE)} data-testid="load-task-csv-sample" className="text-[11px] text-blue-400 hover:text-blue-300">Load sample</button>
              </div>
              <textarea
                value={csv}
                onChange={(e) => setCsv(e.target.value)}
                rows={10}
                placeholder={CSV_SAMPLE}
                data-testid="task-csv-textarea"
                className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm font-mono bg-slate-950/60 text-slate-200"
              />
              <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="task-csv-replace" className="accent-rose-500" />
                Replace existing tasks (clears the project's current task list first)
              </label>
              {result && (
                <div className="text-xs space-y-1" data-testid="task-csv-result">
                  <div className="text-slate-300">
                    {result.dry_run ? "Preview:" : "Imported:"} <strong className="text-emerald-300">{result.created_count}</strong> task{result.created_count === 1 ? "" : "s"}
                    {result.skipped?.length ? <span className="text-amber-300"> · {result.skipped.length} warning{result.skipped.length === 1 ? "" : "s"}</span> : null}
                    {result.replace ? <span className="text-rose-300"> · replaced existing tasks</span> : null}
                  </div>
                  {result.skipped?.length > 0 && (
                    <ul className="text-amber-300/80 list-disc ml-5 max-h-24 overflow-y-auto">
                      {result.skipped.map((s) => <li key={`${s.row}-${s.reason}`}>Row {s.row}: {s.reason}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-800">
              <button onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
              <button onClick={() => run({ dryRun: true })} disabled={busy} data-testid="task-csv-preview-btn" className="border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-2 rounded-lg text-sm">Preview</button>
              <button onClick={() => run({ dryRun: false })} disabled={busy} data-testid="task-csv-import-btn"
                className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {busy ? "Importing…" : "Import to project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
